"use client";

import { AuthRequiredWrapper } from "@/components/auth-required-wrapper";
import { useAuth } from "@/components/features/auth/hooks/use-auth";
import { Button } from "@workspace/ui/components/button";
import { LoadingSpinner } from "@workspace/ui/components/loading-spinner";
import { cn } from "@workspace/ui/lib/utils";
import { Bell, Check } from "lucide-react";
import { useUserFollows } from "../hooks/use-user-follows";

interface UserFollowButtonProps {
  userId: string;
  username: string;
  variant?: "default" | "outline" | "secondary";
  size?: "default" | "sm" | "lg" | "icon";
  className?: string;
  disabled?: boolean;
}

export function UserFollowButton({
  userId,
  username,
  variant = "outline",
  size = "default",
  className = "",
  disabled = false,
}: UserFollowButtonProps) {
  const { user: currentUser } = useAuth();
  const { toggleFollow, isMutating, isFollowing, isLoading } = useUserFollows({
    userId,
    username,
  });

  const handleClick = async function () {
    if (!currentUser?.uid) return;

    toggleFollow();
  };

  const isOwnProfile = userId === currentUser?.uid;

  return (
    <AuthRequiredWrapper
      message="Please sign in to follow users"
      disabled={isMutating}
    >
      <Button
        aria-disabled={isOwnProfile}
        disabled={isMutating || isLoading || isOwnProfile || disabled}
        aria-label={
          isOwnProfile
            ? "You cannot follow yourself"
            : isFollowing
              ? "Unfollow user"
              : "Follow user"
        }
        variant={variant}
        size={size}
        onClick={handleClick}
        className={cn(
          isFollowing
            ? "bg-brand-50 hover:bg-brand-100 text-brand-600"
            : "bg-brand-200 hover:bg-brand-300 text-brand-800",
          className,
        )}
      >
        {isLoading || isMutating ? (
          <LoadingSpinner className="h-4 w-4" />
        ) : isFollowing ? (
          <Check className="text-primary h-4 w-4" />
        ) : (
          <Bell className="h-4 w-4" />
        )}
        {isOwnProfile ? "You" : isFollowing ? "Following" : "Follow"}
      </Button>
    </AuthRequiredWrapper>
  );
}
